import { useState, useMemo, useCallback } from 'react';
import type {
  AvailableCarrier,
  CarrierQuote,
  CarriersVehicle,
  CarriersLocation,
  CarriersInsuranceHistory,
} from 'api/quote-marketplace';
import {
  getAutoTotalMatchCount,
  getPropertyTotalMatchCount,
  type MatchScore,
} from '../utils/coverageMatchScoring';

export type CarrierSortOption = 'price' | 'coverages';

const parsePremium = (premium: string | number | null | undefined): number => {
  if (premium === null || premium === undefined) return Infinity;
  if (typeof premium === 'number') return premium;
  const parsed = parseFloat(premium.replace(/[^0-9.-]+/g, ''));
  return isNaN(parsed) ? Infinity : parsed;
};

const getQuoteCoverageScore = (
  quote: CarrierQuote | undefined,
  vehicles: CarriersVehicle[] | undefined,
  locations: CarriersLocation[] | undefined,
  insuranceHistory: CarriersInsuranceHistory | undefined,
): MatchScore => {
  if (!quote || quote.status !== 'quoted') {
    return { matched: -1, requested: 0 };
  }

  if (vehicles) {
    return getAutoTotalMatchCount(vehicles, quote.rated_coverages?.vehicles);
  }

  if (locations && insuranceHistory) {
    return getPropertyTotalMatchCount(
      locations,
      insuranceHistory,
      quote.rated_coverages?.locations as Record<string, unknown>[] | undefined,
    );
  }

  return { matched: -1, requested: 0 };
};

interface UseCarrierSortingProps {
  carriers: AvailableCarrier[];
  quotes: CarrierQuote[];
  vehicles?: CarriersVehicle[];
  locations?: CarriersLocation[];
  insuranceHistory?: CarriersInsuranceHistory;
  selectedCarrierQuoteId?: number | null;
}

interface UseCarrierSortingReturn {
  sortBy: CarrierSortOption;
  setSortBy: (sort: CarrierSortOption) => void;
  sortedCarriers: AvailableCarrier[];
}

export const useCarrierSorting = ({
  carriers,
  quotes,
  vehicles,
  locations,
  insuranceHistory,
  selectedCarrierQuoteId,
}: UseCarrierSortingProps): UseCarrierSortingReturn => {
  const [sortBy, setSortBy] = useState<CarrierSortOption>('price');

  const sortedCarriers = useMemo(() => {
    const sortByPrice = (a: AvailableCarrier, b: AvailableCarrier): number => {
      const quoteA = quotes.find((q) => q.carrier_id === a.carrier_id);
      const quoteB = quotes.find((q) => q.carrier_id === b.carrier_id);
      return (
        parsePremium(quoteA?.annual_premium) -
        parsePremium(quoteB?.annual_premium)
      );
    };

    let sorted: AvailableCarrier[];

    if (sortBy === 'price') {
      sorted = [...carriers].sort(sortByPrice);
    } else {
      sorted = [...carriers].sort((a, b) => {
        const quoteA = quotes.find((q) => q.carrier_id === a.carrier_id);
        const quoteB = quotes.find((q) => q.carrier_id === b.carrier_id);

        const scoreA = getQuoteCoverageScore(
          quoteA,
          vehicles,
          locations,
          insuranceHistory,
        );
        const scoreB = getQuoteCoverageScore(
          quoteB,
          vehicles,
          locations,
          insuranceHistory,
        );

        if (scoreA.matched !== scoreB.matched) {
          return scoreB.matched - scoreA.matched;
        }

        return (
          parsePremium(quoteA?.annual_premium) -
          parsePremium(quoteB?.annual_premium)
        );
      });
    }

    if (selectedCarrierQuoteId) {
      const selectedIndex = sorted.findIndex((c) => {
        const quote = quotes.find((q) => q.carrier_id === c.carrier_id);
        return quote?.id === selectedCarrierQuoteId;
      });
      if (selectedIndex > 0) {
        const [selected] = sorted.splice(selectedIndex, 1);
        sorted.unshift(selected);
      }
    }

    return sorted;
  }, [
    carriers,
    quotes,
    vehicles,
    locations,
    insuranceHistory,
    sortBy,
    selectedCarrierQuoteId,
  ]);

  const handleSetSortBy = useCallback((sort: CarrierSortOption) => {
    setSortBy(sort);
  }, []);

  return {
    sortBy,
    setSortBy: handleSetSortBy,
    sortedCarriers,
  };
};
